<template>
  <article
    data-event-card-v="3.0.4"
    :class="[$style.config, $style.container]"
    :data-event-id="data?.sys?.id"
    @click="handleWrapClick"
  >
    <BasePicture
      v-if="computedThumbnail"
      :src="computedThumbnail + `?w=570&fit=thumb`"
      alt=""
      :img-class="$style.img"
    />
    <div v-else :class="$style.placeholder">
      <BasePicture
        src="https://images.ctfassets.net/0piqveu8x9oj/2cCEovTiEAye8swWMZkGXj/0a805ace8b7f9df90806ca7a1d99b510/WPN_FullColor_RGB.png"
        alt=""
      />
    </div>
    <div :class="$style.content">
      <BaseHeading size="h3" :class="$style.title">
        <NuxtLinkLocale
          ref="cardLink"
          :to="data?.fields?.path ? `/event/${data?.fields?.path}` : ''"
          :class="$style.titleLink"
          @click.stop="handleClick"
        >
          {{ data?.fields?.title }}
        </NuxtLinkLocale>
      </BaseHeading>

      <EventCountdown
        v-if="data?.fields?.startDatetime && data?.fields?.endDatetime"
        :start-datetime="data?.fields?.startDatetime"
        :end-datetime="data?.fields?.endDatetime"
      />
    </div>
  </article>
</template>

<script setup lang="ts">
import type { TypeEventWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { $handleBlockLinkClick } = useNuxtApp()

const props = defineProps<{
  data: TypeEventWithoutUnresolvableLinksResponse
}>()

const computedThumbnail = computed(() => {
  return (
    props.data?.fields?.thumbnail?.fields?.file?.url ||
    props.data?.fields?.featuredImage?.fields?.file?.url
  )
})

const cardLink = useTemplateRef('cardLink')

const handleWrapClick = (event: MouseEvent) => {
  const cardLinkEl = cardLink.value?.$el
  if (cardLinkEl) $handleBlockLinkClick(event, cardLinkEl)
}

const handleClick = (event: MouseEvent) => {
  const cardLinkEl = cardLink.value?.$el
  if (cardLinkEl) $handleBlockLinkClick(event, cardLinkEl)

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'event_click',
    eventParams: {
      event_name: props.data?.fields?.entryTitle,
    },
  })
}
</script>

<style lang="scss" module>
.container {
  cursor: pointer;
  background: #fff;
  border: 1px solid var(--gray-4);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
  display: grid;
  gap: 10px;
  text-align: center;
  transition: cubic-bezier(0.16, 1, 0.29, 0.99) 0.2s;
  min-height: 134px;
  @include fluid-type(21px, 23px);

  @include media('xs') {
    grid-template-columns: 200px 1fr;
    gap: 20px;
    text-align: left;
  }

  @include media('lg') {
    gap: 30px;
  }

  &:hover,
  &:focus {
    border: 1px solid var(--gray-5);
    box-shadow: 0 4px 13px rgba(#000, 0.12);
  }
}

.content {
  padding: 16px;
}

.placeholder {
  background: #504e8b;

  display: grid;
  place-content: center;
  padding: 0 2em;

  img {
    filter: brightness(10); // make the logo white
  }
}

.img {
  width: 100%;
  vertical-align: middle;

  @include media('xs') {
    object-fit: cover;
    // height: 100%; // this causes issue on safari
    aspect-ratio: 200 / 144;
    min-height: 100%;
    width: auto;
  }
}

.title {
  font-family: var(--roboto-condensed);
  font-size: 25px;
  line-height: math.div(30, 25);
  margin-bottom: 3px;
}

.titleLink {
  color: inherit;
  text-decoration: none;
}

.eventDate {
  font-size: to-rem(17px);
  line-height: math.div(27px, 17px);
}

.progressBar {
  --progress-bar-background-color: #{rgba(#000, 0.11)};

  max-width: 170px;
  margin: 14px auto 5px;

  @include media('xs') {
    margin-left: 0;
    margin-right: 0;
  }
}

.dateDistance {
  font-size: to-rem(14px);
  line-height: math.div(28px, 14px);
  font-style: italic;
  font-family: var(--georgia);
}

.cta {
  margin-top: 17px;
}
</style>
